import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaBuilding, 
  FaComment, 
  FaMapMarkerAlt, 
  FaClock,
  FaFacebook,
  FaTwitter,
  FaLinkedin,
  FaInstagram
} from 'react-icons/fa';
import './GeneralPageStyles.css';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }

    if (!formData.subject) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.message) {
      newErrors.message = 'Message is required';
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt />,
      title: "Our Office",
      details: [
        "123 Financial District",
        "New York, NY 10001",
        "United States"
      ]
    },
    {
      icon: <FaPhone />,
      title: "Phone Numbers",
      details: [
        "+1 (555) 123-4567",
        "+1 (555) 987-6543"
      ]
    },
    {
      icon: <FaEnvelope />,
      title: "Email Addresses",
      details: [
        "support@upmtraders.com",
        "info@upmtraders.com"
      ]
    },
    {
      icon: <FaClock />,
      title: "Working Hours",
      details: [
        "Monday - Friday: 9AM - 6PM",
        "Saturday: 10AM - 4PM",
        "Sunday: Closed"
      ]
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    visible: { 
      transition: { 
        staggerChildren: 0.1 
      } 
    }
  };

  const floatAnimation = {
    y: [0, -5, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  if (isSubmitted) {
    return (
      <div className="page-content">
        <div className="page-header">
          <div className="container">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Contact Us
            </motion.h1>
          </div>
        </div>
        
        <div className="container">
          <motion.div 
            className="form-container"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="success-message">
              <motion.h2
                animate={floatAnimation}
              >
                Thank You!
              </motion.h2>
              <p>Your message has been sent successfully. We'll get back to you soon.</p>
              <motion.button 
                className="btn btn-brand"
                onClick={() => setIsSubmitted(false)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Send Another Message
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Contact Us
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            We'd love to hear from you. Get in touch with our team.
          </motion.p>
        </div>
      </div>
      
      <div className="container">
        <div className="contact-grid">
          <motion.div 
            className="contact-info"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={staggerChildren}
            ref={ref}
          >
            {contactInfo.map((info, index) => (
              <motion.div 
                className="contact-detail" 
                key={index}
                variants={fadeInUp}
                whileHover={{ 
                  x: 10,
                  transition: { duration: 0.3 }
                }}
              >
                <motion.div 
                  className="contact-icon"
                  animate={floatAnimation}
                >
                  {info.icon}
                </motion.div>
                <div>
                  <motion.h3
                    animate={floatAnimation}
                    transition={{ delay: index * 0.1 }}
                  >
                    {info.title}
                  </motion.h3>
                  <ul>
                    {info.details.map((detail, detailIndex) => (
                      <motion.li 
                        key={detailIndex}
                        initial={{ opacity: 0, x: -20 }}
                        animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        transition={{ delay: detailIndex * 0.1 }}
                      >
                        {detail}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
            
            <motion.div 
              className="social-links"
              variants={fadeInUp}
            >
              <motion.a 
                href="#" 
                aria-label="Facebook"
                whileHover={{ 
                  y: -5,
                  color: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <FaFacebook />
              </motion.a>
              <motion.a 
                href="#" 
                aria-label="Twitter"
                whileHover={{ 
                  y: -5,
                  color: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <FaTwitter />
              </motion.a>
              <motion.a 
                href="#" 
                aria-label="LinkedIn"
                whileHover={{ 
                  y: -5,
                  color: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <FaLinkedin />
              </motion.a>
              <motion.a 
                href="#" 
                aria-label="Instagram"
                whileHover={{ 
                  y: -5,
                  color: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <FaInstagram />
              </motion.a>
            </motion.div>
          </motion.div>
          
          <motion.div 
            className="form-container"
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            variants={fadeInUp}
          >
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <div className="input-icon">
                  <FaUser />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-control ${errors.name ? 'error' : ''}`}
                />
                {errors.name && <div className="error-message">{errors.name}</div>}
              </div>
              
              <div className="form-group">
                <div className="input-icon">
                  <FaEnvelope />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-control ${errors.email ? 'error' : ''}`}
                />
                {errors.email && <div className="error-message">{errors.email}</div>}
              </div>
              
              <div className="form-group">
                <div className="input-icon">
                  <FaPhone />
                </div>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  placeholder="Phone Number (Optional)"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <div className="input-icon">
                  <FaBuilding />
                </div>
                <input
                  type="text"
                  id="company"
                  name="company"
                  placeholder="Company (Optional)"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <div className="input-icon">
                  <FaComment />
                </div>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`form-control ${errors.subject ? 'error' : ''}`}
                />
                {errors.subject && <div className="error-message">{errors.subject}</div>}
              </div>
              
              <div className="form-group">
                <div className="input-icon">
                  <FaComment />
                </div>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Your Message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  className={`form-control ${errors.message ? 'error' : ''}`}
                ></textarea>
                {errors.message && <div className="error-message">{errors.message}</div>}
              </div>
              
              <motion.button 
                type="submit" 
                className="btn btn-brand btn-block"
                disabled={isSubmitting}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;